import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from './Notification';
import { useLanguage } from '../context/LanguageContext';
import { useGeoCountry } from '../hooks/useGeoCountry';
import { countryToCode, getCountryName } from '../data/euCountries';
import CountrySelect from './CountrySelect';
import './AddressManager.css';

function AddressManager() {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { countryCode: geoCountryCode } = useGeoCountry(language);
  const geoAppliedRef = useRef(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    address_type: 'shipping',
    is_default: false,
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'LT',
    phone: '',
    email: ''
  });

  const { data: addresses, isLoading } = useQuery({
    queryKey: ['user-addresses'],
    queryFn: async () => {
      const res = await apiClient.get('/user/addresses');
      return res.data;
    },
  });

  const createAddressMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/user/addresses', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-addresses']);
      setShowForm(false);
      resetForm();
      notification.success(t('addressAdded'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorAddingAddress'));
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/user/addresses/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-addresses']);
      setShowForm(false);
      setEditingAddress(null);
      resetForm();
      notification.success(t('addressUpdated'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorUpdatingAddress'));
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/user/addresses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-addresses']);
      notification.success(t('addressRemoved'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorRemovingAddress'));
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.post(`/user/addresses/${id}/set-default`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-addresses']);
      notification.success(t('defaultAddressSet'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorSettingDefault'));
    },
  });

  const resetForm = () => {
    setFormData({
      address_type: 'shipping',
      is_default: false,
      first_name: '',
      last_name: '',
      company: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: geoCountryCode || 'LT',
      phone: '',
      email: ''
    });
  };

  useEffect(() => {
    if (!showForm) geoAppliedRef.current = false;
    if (showForm && !editingAddress && geoCountryCode && !geoAppliedRef.current) {
      geoAppliedRef.current = true;
      setFormData((prev) => ({ ...prev, country: geoCountryCode }));
    }
  }, [showForm, editingAddress, geoCountryCode]);

  const handleEdit = (address) => {
    setEditingAddress(address.id);
    setFormData({
      address_type: address.address_type,
      is_default: address.is_default,
      first_name: address.first_name,
      last_name: address.last_name,
      company: address.company || '',
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state || '',
      postal_code: address.postal_code,
      country: countryToCode(address.country) || 'LT',
      phone: address.phone || '',
      email: address.email || ''
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingAddress) {
      updateAddressMutation.mutate({ id: editingAddress, data: formData });
    } else {
      createAddressMutation.mutate(formData);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await notification.showConfirm(t('confirmDeleteAddress'), t('delete'), 'danger');
    if (confirmed) {
      deleteAddressMutation.mutate(id);
    }
  };

  const handleSetDefault = (id) => {
    setDefaultMutation.mutate(id);
  };

  if (isLoading) {
    return <div className="loading">{t('loading')}</div>;
  }

  return (
    <div className="address-manager">
      <div className="address-manager-header">
        <h2>{t('myAddresses')}</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setEditingAddress(null);
            setShowForm(true);
          }}
        >
          + {t('addNewAddress')}
        </button>
      </div>

      {showForm && (
        <div className="address-form-modal">
          <div className="address-form-content">
            <h3>{editingAddress ? t('editAddress') : t('addNewAddress')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('addressType')} *</label>
                <select
                  value={formData.address_type}
                  onChange={(e) => setFormData({ ...formData, address_type: e.target.value })}
                  required
                  className="input"
                >
                  <option value="shipping">{t('shippingAddress')}</option>
                  <option value="billing">{t('billingAddress')}</option>
                  <option value="both">{t('bothAddressTypes')}</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('firstName')} *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('lastName')} *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('company')}</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>{t('address')} *</label>
                <input
                  type="text"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  required
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>{t('addressLine2')}</label>
                <input
                  type="text"
                  value={formData.address_line2}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  className="input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('city')} *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('postalCode')} *</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    required
                    className="input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('country')} *</label>
                  <CountrySelect
                    value={formData.country}
                    onChange={(code) => setFormData({ ...formData, country: code })}
                    language={language}
                    className="input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  {t('setAsDefaultAddress')}
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createAddressMutation.isLoading || updateAddressMutation.isLoading}
                >
                  {editingAddress ? t('saveChanges') : t('addAddress')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAddress(null);
                    resetForm();
                  }}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="addresses-list">
        {addresses && addresses.length > 0 ? (
          addresses.map((address) => (
            <div key={address.id} className={`address-card ${address.is_default ? 'default' : ''}`}>
              <div className="address-header">
                <h4>
                  {address.first_name} {address.last_name}
                  {address.is_default ? <span className="default-badge">{t('defaultAddress')}</span> : null}
                </h4>
                <div className="address-type">
                  {address.address_type === 'shipping' || address.address_type === 0
                    ? t('shippingAddress')
                    : address.address_type === 'billing' || address.address_type === 1
                      ? t('billingAddress')
                      : address.address_type === 'both' || address.address_type === 2
                        ? t('bothAddressesLabel')
                        : null}
                </div>
              </div>
              <div className="address-details">
                {address.company && <p><strong>{t('company')}:</strong> {address.company}</p>}
                <p>{address.address_line1}</p>
                {address.address_line2 && <p>{address.address_line2}</p>}
                <p>{address.city}, {address.postal_code}</p>
                <p>{getCountryName(countryToCode(address.country), language)}</p>
                {address.phone && <p><strong>{t('phone')}:</strong> {address.phone}</p>}
                {address.email && <p><strong>{t('email')}:</strong> {address.email}</p>}
              </div>
              <div className="address-actions">
                {!address.is_default && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleSetDefault(address.id)}
                    disabled={setDefaultMutation.isLoading}
                  >
                    {t('setAsDefault')}
                  </button>
                )}
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleEdit(address)}
                >
                  {t('edit')}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(address.id)}
                  disabled={deleteAddressMutation.isLoading}
                >
                  {t('remove')}
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-addresses">{t('noAddressesMessage')}</p>
        )}
      </div>
    </div>
  );
}

export default AddressManager;
